import type { AvatarPresetId } from './avatarPresets'
import type { League } from './types'

/**
 * The leagues payload resolves each member's avatar server-side at request
 * time, so a saved avatar only reaches the league member rows once `/leagues` is refetched.
 * Extracted from App so the ordering/rollback rules below are unit-testable.
 */
export interface AvatarChangeDeps {
  /** The preset shown before the optimistic update, restored if the save fails. */
  previous: AvatarPresetId | null
  setAvatarPreset: (preset: AvatarPresetId | null) => void
  updateAvatarPreset: (preset: AvatarPresetId | null) => Promise<unknown>
  loadLeagues: () => Promise<League[]>
  setLeagues: (leagues: League[]) => void
  setToastMessage: (message: string) => void
}

export async function changeAvatarPreset(
  preset: AvatarPresetId | null,
  deps: AvatarChangeDeps
): Promise<void> {
  deps.setAvatarPreset(preset) // optimistic — Profile already shows it instantly
  try {
    await deps.updateAvatarPreset(preset)
  } catch {
    deps.setAvatarPreset(deps.previous)
    deps.setToastMessage('Failed to update avatar. Please try again.')
    return
  }
  // The avatar *did* save, so a failed refetch must not roll it back or toast — the
  // league rows just stay stale until the next load.
  try {
    deps.setLeagues(await deps.loadLeagues())
  } catch {
    /* ignore: secondary refresh only */
  }
}
